import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TableRowsIcon from '@mui/icons-material/TableRowsRounded';
import InsightsIcon from '@mui/icons-material/InsightsRounded';
import RestartAltIcon from '@mui/icons-material/RestartAltRounded';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import moment from 'moment';
import 'moment/locale/fr';
import toast from 'react-hot-toast';

import { formateurSurveyApi } from '../../api/formateurSurveyApi';
import { formationApi } from '../../api/formationApi';
import Scrollbar from '../../components/Scrollbar';
import ScrollJumpButtons, { findScrollParent } from '../../components/ScrollJumpButtons';
import AnswerBar from '../../components/dashboard/questionnaire/responses/AnswerBar';
import FormateurResponseDrawer from '../../components/dashboard/questionnaire/responses/FormateurResponseDrawer';
import StarRating from '../../components/dashboard/questionnaire/responses/StarRating';
import ResponsesTabs from '../../components/dashboard/questionnaire/responses/ResponsesTabs';
import {
  FORMATEUR_TAB,
  formatDateRange,
  formatStyle,
  scoreColor,
  sessionStatusStyle
} from '../../components/dashboard/questionnaire/responses/constants';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import { alpha } from '@mui/material/styles';
import { grey, lightVeil, greyAlpha } from '../../theme/tones';

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Réponse la plus récente' },
  { value: 'date_asc', label: 'Réponse la plus ancienne' },
  { value: 'session_desc', label: 'Session la plus récente' },
  { value: 'score_desc', label: 'Meilleure note' },
  { value: 'score_asc', label: 'Note la plus faible' },
  { value: 'formateur_asc', label: 'Formateur (A → Z)' },
  { value: 'formation_asc', label: 'Formation (A → Z)' }
];

const EMPTY_FILTERS = {
  formation_id: 'All',
  intervenant_id: 'All',
  year: 'All',
  scope: 'All',
  search: ''
};

const sortRows = (rows, sortBy) => {
  const list = [...rows];
  const answeredAt = (r) => (r.created_at ? moment(r.created_at).valueOf() : 0);
  const sessionAt = (r) => (r.start_date ? moment(r.start_date).valueOf() : 0);
  switch (sortBy) {
    case 'date_asc': return list.sort((a, b) => answeredAt(a) - answeredAt(b));
    case 'session_desc': return list.sort((a, b) => sessionAt(b) - sessionAt(a));
    case 'score_desc': return list.sort((a, b) => (b.average ?? -1) - (a.average ?? -1));
    case 'score_asc': return list.sort((a, b) => (a.average ?? 101) - (b.average ?? 101));
    case 'formateur_asc': return list.sort((a, b) => (a.formateur || '').localeCompare(b.formateur || ''));
    case 'formation_asc': return list.sort((a, b) => (a.formation_name || '').localeCompare(b.formation_name || ''));
    default: return list.sort((a, b) => answeredAt(b) - answeredAt(a));
  }
};

/* ------------------------------------------------------------------ */
/* KPI                                                                 */
/* ------------------------------------------------------------------ */

const KpiCard = ({ label, value, suffix, hint, color, progress, badge }) => (
  <Card
    sx={{
      p: 2.25,
      borderRadius: '18px',
      border: `1px solid ${grey(100)}`,
      boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }}
  >
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em', color: 'text.disabled', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography sx={{ fontSize: '1.9rem', fontWeight: 900, color: color || grey(900), lineHeight: 1.1 }}>
          {value}
        </Typography>
        {suffix && (
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: color || grey(900) }}>{suffix}</Typography>
        )}
      </Box>
      {badge}
    </Box>
    {progress !== undefined && progress !== null && (
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, progress))}
        sx={{
          height: 5,
          borderRadius: 3,
          mt: 0.5,
          bgcolor: (theme) => alpha(theme.palette.text.disabled, 0.16),
          '& .MuiLinearProgress-bar': { bgcolor: color || grey(900), borderRadius: 3 }
        }}
      />
    )}
    {hint && (
      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600, mt: progress !== undefined ? 0.25 : 0 }}>
        {hint}
      </Typography>
    )}
  </Card>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

/**
 * Réponses aux questionnaires de satisfaction des FORMATEURS.
 *
 * Page distincte de celle des apprenants, mais présentée comme un onglet de
 * plus — c'est le même geste : « je viens lire les réponses aux
 * questionnaires ». Deux différences dictent tout le reste :
 *
 *  - une session n'a qu'un formateur : il n'y a rien à moyenner au niveau
 *    session, la vue est donc une liste à plat de réponses, sans le double
 *    niveau formation → sessions des apprenants ;
 *  - les deux modèles (présentiel / classe virtuelle) sont communs à toutes
 *    les formations : les réponses s'additionnent question par question sur
 *    tout le parc, ce qui est impossible côté apprenant. D'où l'onglet
 *    « Synthèse », qui est le vrai apport de cette page.
 */
const QuestionnaireFormateurs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // `formation` dans l'URL : la formation sur laquelle on était de l'autre côté
  // de la barre d'onglets. Ici elle ne déplie pas un niveau — la vue est à plat
  // — mais pré-remplit le filtre, ce qui revient au même à l'écran.
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    formation_id: searchParams.get('formation') || 'All'
  }));
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [view, setView] = useState(() => (searchParams.get('vue') === 'synthese' ? 'synthese' : 'reponses'));

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ years: [], scopes: [], formateurs: [] });
  const [loading, setLoading] = useState(true);

  const [scopes, setScopes] = useState([]);
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [synthesisLoadedFor, setSynthesisLoadedFor] = useState(null);

  const [formations, setFormations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const accent = FORMATEUR_TAB.accent;
  const loadedOptions = useRef(false);
  const requestId = useRef(0);

  /* --- chargement ------------------------------------------------- */

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        // Liste allégée : seuls l'identifiant et le libellé alimentent le filtre.
        setFormations((await formationApi.getFormationOptions(accessToken)) || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const queryFilters = useMemo(() => ({
    formation_id: filters.formation_id,
    intervenant_id: filters.intervenant_id,
    year: filters.year,
    scope: filters.scope,
    search: debouncedSearch
  }), [filters.formation_id, filters.intervenant_id, filters.year, filters.scope, debouncedSearch]);

  const filterSignature = useMemo(() => JSON.stringify(queryFilters), [queryFilters]);

  useEffect(() => {
    const controller = new AbortController();
    const current = ++requestId.current;

    (async () => {
      try {
        setLoading(true);
        const accessToken = window.localStorage.getItem('accessToken');
        const res = await formateurSurveyApi.fetchOverview(
          queryFilters,
          accessToken,
          !loadedOptions.current,
          controller.signal
        );
        if (current !== requestId.current) return;

        if (res && res.status) {
          setRows(res.rows || []);
          setStats(res.stats || null);
          if (res.options) {
            setOptions(res.options);
            loadedOptions.current = true;
          }
        } else {
          setRows([]);
          setStats(null);
          if (res && res.message) toast.error(res.message);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [queryFilters, refreshToken]);

  // La synthèse n'est demandée qu'à l'ouverture de son onglet, puis à chaque
  // changement de filtre : c'est le calcul le plus lourd des deux.
  useEffect(() => {
    if (view !== 'synthese') return undefined;
    const signature = `${filterSignature}-${refreshToken}`;
    if (synthesisLoadedFor === signature) return undefined;

    const controller = new AbortController();
    (async () => {
      try {
        setSynthesisLoading(true);
        const accessToken = window.localStorage.getItem('accessToken');
        const res = await formateurSurveyApi.fetchSynthesis(queryFilters, accessToken, controller.signal);
        if (res && res.status) {
          setScopes(res.scopes || []);
          setSynthesisLoadedFor(signature);
        } else if (res && res.message) {
          toast.error(res.message);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      } finally {
        setSynthesisLoading(false);
      }
    })();

    return () => controller.abort();
  }, [view, queryFilters, filterSignature, refreshToken, synthesisLoadedFor]);

  // La vue courante et la formation filtrée vivent dans l'URL : un lien partagé
  // rouvre la synthèse, et le retour vers les onglets apprenant retrouve la
  // formation même après un rafraîchissement.
  useEffect(() => {
    const next = new URLSearchParams();
    if (view === 'synthese') next.set('vue', 'synthese');
    if (filters.formation_id && filters.formation_id !== 'All') {
      next.set('formation', String(filters.formation_id));
    }
    if (next.toString() !== window.location.search.replace(/^\?/, '')) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, filters.formation_id]);

  /* --- interactions ------------------------------------------------ */

  const patchFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setDebouncedSearch('');
    setSortBy('date_desc');
  };

  const activeFilterCount = useMemo(() => (
    Object.entries(filters).filter(([key, value]) => value !== EMPTY_FILTERS[key]).length
  ), [filters]);

  const sortedRows = useMemo(() => sortRows(rows, sortBy), [rows, sortBy]);

  const refresh = useCallback(() => {
    setSynthesisLoadedFor(null);
    setRefreshToken((n) => n + 1);
  }, []);

  const anchorRef = useRef(null);
  const getPageScroller = useCallback(() => findScrollParent(anchorRef.current), []);

  /* --- rendu -------------------------------------------------------- */

  const inputSx = {
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-root': { borderRadius: '12px' }
  };

  return (
    <>
      <Helmet>
        <title>Réponses des formateurs | Qualité | Apteed</title>
      </Helmet>

      <Box ref={anchorRef} sx={{ bgcolor: 'action.hover', minHeight: '100%', py: 3 }}>
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>

          {/* ── En-tête ───────────────────────────────────────────── */}
          <Box
            sx={{
              mb: 2.5,
              p: 3,
              borderRadius: '24px',
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              background: `linear-gradient(135deg, ${lightVeil(0.96)} 0%, ${greyAlpha(100, 0.75)} 100%)`,
              boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box
                sx={{
                  width: 54, height: 54, borderRadius: '16px', flexShrink: 0,
                  background: FORMATEUR_TAB.gradient,
                  display: 'grid', placeItems: 'center',
                  boxShadow: `0 8px 20px ${accent}4D`
                }}
              >
                <CoPresentIcon sx={{ color: grey(0), fontSize: '1.6rem' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.45rem', color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  Résultats des questionnaires
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.87rem', mt: 0.5 }}>
                  {FORMATEUR_TAB.hint}
                  {stats?.last_response_at && ` · Dernière réponse ${moment(stats.last_response_at).fromNow()}`}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/dashboard"
                startIcon={<ArrowLeftIcon style={{ fontSize: 13 }} />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
              >
                Retour
              </Button>
              <Button
                component={RouterLink}
                to="/dashboard/intervenants"
                variant="outlined"
                startIcon={<CoPresentIcon sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, borderColor: 'divider', color: 'text.primary' }}
              >
                Intervenants
              </Button>
            </Box>
          </Box>

          {/* ── Sélecteur d'onglet ────────────────────────────────── */}
          <ResponsesTabs
            active={FORMATEUR_TAB.value}
            context={{ formationId: filters.formation_id !== 'All' ? filters.formation_id : null }}
          />

          {/* ── KPI ───────────────────────────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6} md={3}>
              <KpiCard
                label="Satisfaction moyenne"
                value={stats?.average !== null && stats?.average !== undefined ? stats.average.toFixed(1) : '—'}
                suffix={stats?.average !== null && stats?.average !== undefined ? '%' : ''}
                color={scoreColor(stats?.average)}
                progress={stats?.average ?? 0}
                badge={<StarRating percent={stats?.average} size="0.95rem" iconSize="1.15rem" />}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard label="Réponses" value={stats?.responses ?? 0} color={accent} hint="Questionnaires remplis" />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard label="Formateurs" value={stats?.formateurs ?? 0} color={grey(900)} hint="Répondants distincts" />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard
                label="Sessions couvertes"
                value={stats?.sessions ?? 0}
                color={grey(900)}
                hint={`sur ${stats?.formations ?? 0} formation${(stats?.formations ?? 0) > 1 ? 's' : ''}`}
              />
            </Grid>
          </Grid>

          {/* ── Filtres ───────────────────────────────────────────── */}
          <Card sx={{ p: 2, mb: 2.5, borderRadius: '18px', border: `1px solid ${grey(100)}`, boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}` }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Formation, formateur, e-mail…"
                value={filters.search}
                onChange={(e) => patchFilters({ search: e.target.value })}
                sx={{ ...inputSx, flex: '1 1 260px', minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }}
              />

              <Autocomplete
                size="small"
                options={formations}
                value={formations.find((f) => String(f.id) === String(filters.formation_id)) || null}
                getOptionLabel={(o) => (o ? `${o.name}${o.programNumberAll ? ` (N° ${o.programNumberAll})` : ''}`.trim() : '')}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(e, value) => patchFilters({ formation_id: value ? value.id : 'All' })}
                sx={{ flex: '2 1 320px', minWidth: 260 }}
                renderInput={(params) => (
                  <TextField {...params} label="Formation" variant="outlined" sx={inputSx} />
                )}
              />

              <TextField
                select size="small" label="Formateur" value={filters.intervenant_id}
                onChange={(e) => patchFilters({ intervenant_id: e.target.value })}
                sx={{ ...inputSx, flex: '1 1 210px', minWidth: 180 }}
              >
                <MenuItem value="All">Tous</MenuItem>
                {options.formateurs.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Année" value={filters.year}
                onChange={(e) => patchFilters({ year: e.target.value })}
                sx={{ ...inputSx, flex: '0 1 120px', minWidth: 110 }}
              >
                <MenuItem value="All">Toutes</MenuItem>
                {options.years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Modèle" value={filters.scope}
                onChange={(e) => patchFilters({ scope: e.target.value })}
                sx={{ ...inputSx, flex: '0 1 180px', minWidth: 160 }}
              >
                <MenuItem value="All">Tous</MenuItem>
                {options.scopes.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Box>

            <Divider sx={{ my: 1.75, borderColor: 'divider' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'text.primary' }}>
                {loading ? 'Chargement…' : `${sortedRows.length} réponse${sortedRows.length > 1 ? 's' : ''}`}
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} filtre${activeFilterCount > 1 ? 's' : ''} actif${activeFilterCount > 1 ? 's' : ''}`}
                  size="small"
                  onDelete={resetFilters}
                  deleteIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
                  sx={{ bgcolor: `${accent}1A`, color: accent, fontWeight: 800, fontSize: '0.68rem', height: 22 }}
                />
              )}

              <Box sx={{ flexGrow: 1 }} />

              {view === 'reponses' && (
                <TextField
                  select size="small" label="Trier par" value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ ...inputSx, minWidth: 210 }}
                >
                  {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              )}

              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(e, v) => v && setView(v)}
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiToggleButton-root': { border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: '10px !important', px: 1.25 },
                  '& .Mui-selected': { bgcolor: `${accent}1A !important`, color: `${accent} !important` }
                }}
              >
                <ToggleButton value="reponses"><Tooltip title="Réponses"><TableRowsIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
                <ToggleButton value="synthese"><Tooltip title="Synthèse par question"><InsightsIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Card>

          {/* ── Résultats ─────────────────────────────────────────── */}
          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 12 }}>
              <CircularProgress sx={{ color: accent }} />
            </Box>
          )}

          {!loading && sortedRows.length === 0 && (
            <Card sx={{ py: 10, textAlign: 'center', borderRadius: '20px', border: `1px solid ${grey(100)}`, boxShadow: 'none' }}>
              <CoPresentIcon sx={{ fontSize: 44, color: grey(300), mb: 1.5 }} />
              <Typography sx={{ fontWeight: 800, color: 'text.secondary', mb: 0.5 }}>
                Aucune réponse de formateur
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled', mb: 2.5 }}>
                {activeFilterCount > 0
                  ? 'Aucune réponse ne correspond aux filtres sélectionnés.'
                  : 'Le questionnaire part au formateur la veille de la session ; les réponses apparaîtront ici.'}
              </Typography>
              {activeFilterCount > 0 && (
                <Button
                  onClick={resetFilters}
                  startIcon={<RestartAltIcon sx={{ fontSize: 17 }} />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: accent }}
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </Card>
          )}

          {/* Vue « Réponses » : une ligne par réponse. */}
          {!loading && sortedRows.length > 0 && view === 'reponses' && (
            <Card sx={{ borderRadius: '18px', border: `1px solid ${grey(100)}`, boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`, overflow: 'hidden' }}>
              <Scrollbar>
                <Box sx={{ minWidth: 1040 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Formateur', 'Formation', 'Session', 'Dates', 'Modèle', 'Satisfaction', 'Répondu le', ''].map((h, i) => (
                          <TableCell
                            key={h || i}
                            align={h === 'Satisfaction' ? 'center' : 'left'}
                            sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', bgcolor: 'action.hover', whiteSpace: 'nowrap' }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRows.map((r) => {
                        const color = scoreColor(r.average);
                        const fmt = formatStyle(r.scope || r.format);
                        const status = sessionStatusStyle(r.session_status);
                        const answeredAt = moment(r.created_at);
                        return (
                          <TableRow key={r.id} hover onClick={() => setSelectedId(r.id)} sx={{ cursor: 'pointer' }}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.35 }}>
                                {r.formateur || 'Formateur inconnu'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 600 }}>
                                {r.email || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 340 }}>
                              <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 700, lineHeight: 1.35 }}>
                                {r.formation_name || 'Formation inconnue'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: 'text.primary' }}>
                                  N° {r.session_number ?? '—'}
                                </Typography>
                                {r.session_status && (
                                  <Chip
                                    label={r.session_status}
                                    size="small"
                                    sx={{ bgcolor: status.bg, color: status.color, fontWeight: 800, fontSize: '0.6rem', height: 18 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {formatDateRange(r.start_date, r.end_date)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={r.scope || '—'}
                                size="small"
                                sx={{ bgcolor: fmt.bg, color: fmt.color, fontWeight: 800, fontSize: '0.63rem', height: 20 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 54, height: 6, borderRadius: 3, bgcolor: (theme) => alpha(theme.palette.text.disabled, 0.18), overflow: 'hidden' }}>
                                    <Box sx={{ width: `${Math.max(0, Math.min(100, r.average ?? 0))}%`, height: '100%', bgcolor: color }} />
                                  </Box>
                                  <Typography sx={{ fontWeight: 900, fontSize: '0.78rem', color, minWidth: 42 }}>
                                    {r.average === null || r.average === undefined ? '—' : `${r.average}%`}
                                  </Typography>
                                </Box>
                                <StarRating percent={r.average} size="0.72rem" color={color} iconSize="0.9rem" />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.76rem', color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {answeredAt.isValid() ? answeredAt.format('DD/MM/YYYY HH:mm') : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" sx={{ color: accent }}>
                                <ArrowForwardIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Scrollbar>
            </Card>
          )}

          {/* Vue « Synthèse » : les réponses additionnées question par question. */}
          {!loading && sortedRows.length > 0 && view === 'synthese' && (
            <>
              {synthesisLoading && (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: accent }} />
                </Box>
              )}

              {!synthesisLoading && scopes.map((scope) => (
                <Box key={scope.questionnaire_id} sx={{ mb: 3.5 }}>
                  <Card
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: '18px',
                      border: `1px solid ${grey(100)}`,
                      boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Chip
                      label={scope.scope || 'Modèle'}
                      size="small"
                      sx={{
                        bgcolor: formatStyle(scope.scope).bg,
                        color: formatStyle(scope.scope).color,
                        fontWeight: 800,
                        fontSize: '0.68rem'
                      }}
                    />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'text.primary' }}>
                      {scope.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled', fontWeight: 700 }}>
                      {scope.responses} réponse{scope.responses > 1 ? 's' : ''} · {scope.questions.length} questions
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: scoreColor(scope.average) }}>
                        {scope.average === null ? '—' : `${scope.average}%`}
                      </Typography>
                      <StarRating percent={scope.average} size="0.8rem" color={scoreColor(scope.average)} iconSize="1rem" />
                    </Box>
                  </Card>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2
                    }}
                  >
                    {scope.questions.map((q) => (
                      <Box
                        key={q.id}
                        sx={{ p: 2.25, bgcolor: 'background.paper', borderRadius: '18px', border: `1px solid ${grey(200)}` }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: 'text.primary', flexGrow: 1 }}>
                            {q.text}
                          </Typography>
                          {q.average !== null && (
                            <Box
                              sx={{
                                px: 1.2, py: 0.3, borderRadius: '8px', flexShrink: 0,
                                bgcolor: `${scoreColor(q.average)}1A`,
                                color: scoreColor(q.average),
                                fontWeight: 900, fontSize: '0.74rem'
                              }}
                            >
                              {q.average}%
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 700, mt: 0.5, mb: 1.5 }}>
                          {q.answered} réponse{q.answered > 1 ? 's' : ''}
                          {q.type === 'open' && ' · réponses libres'}
                        </Typography>
                        <Divider sx={{ mb: 1.5, borderColor: 'divider' }} />

                        {q.answered === 0 && (
                          <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>Aucune réponse.</Typography>
                        )}

                        {q.type === 'choice' && q.choices.map((c) => (
                          <AnswerBar
                            key={c.text}
                            label={c.text}
                            count={c.count}
                            total={q.answered}
                            accent={accent}
                            hint={c.weight === null || c.weight === undefined ? null : `${c.weight} pts`}
                          />
                        ))}

                        {q.type === 'open' && q.verbatims.slice(0, 8).map((v) => (
                          <Box key={`${v.submission_id}-${v.answer}`} sx={{ mb: 1.25 }}>
                            <Typography
                              sx={{
                                fontSize: '0.78rem', color: 'text.primary', pl: 1.5,
                                borderLeft: `3px solid ${accent}40`, fontStyle: 'italic', whiteSpace: 'pre-wrap'
                              }}
                            >
                              « {v.answer} »
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 700, pl: 1.5, mt: 0.25 }}>
                              {v.formateur}
                              {v.formation_name ? ` · ${v.formation_name}` : ''}
                              {v.session_number ? ` · S${v.session_number}` : ''}
                            </Typography>
                          </Box>
                        ))}

                        {q.type === 'open' && q.verbatims.length > 8 && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 700, mt: 0.5 }}>
                            +{q.verbatims.length - 8} autre{q.verbatims.length - 8 > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Container>

        <ScrollJumpButtons
          getTarget={getPageScroller}
          watch={`${view}-${sortedRows.length}-${loading}`}
          accent={accent}
          offset={24}
          fixed
        />
      </Box>

      <FormateurResponseDrawer
        submissionId={selectedId}
        accent={accent}
        onClose={() => setSelectedId(null)}
        onDeleted={refresh}
      />
    </>
  );
};

export default QuestionnaireFormateurs;
